<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="2">
        <SelectYear v-model="selectedYear" />
      </v-col>
    </v-row>

    <!-- Přepočet VT a Rankingu -->
    <v-row class="mb-3">
      <v-col cols="12" md="6">
        <v-select
          :items="eventOptions"
          v-model="selectedEvent"
          density="comfortable"
          label="Vyber soutěž"
          hide-details
          item-title="label"
          item-value="value"
          :loading="loading"
          no-data-text="Nenalezeny žádné soutěže v tomto roce"
          loading-text="Načítám soutěže..."
        />
      </v-col>

      <v-col cols="12" md="2" class="d-flex align-end">
        <v-btn
          color="primary"
          :loading="posting"
          :disabled="!selectedEvent"
          @click="recalculateVtRank"
        >
          Přepočítat VT
        </v-btn>
      </v-col>
    </v-row>

    <!-- Uzamčení / odemčení soutěže -->
    <v-row class="mb-3">
      <v-col cols="12" md="6">
        <v-select
          :items="eventOptionsLock"
          v-model="selectedEventLock"
          density="comfortable"
          label="Vyber soutěž k uzamčení/odemčení"
          hide-details
          item-title="label"
          item-value="value"
          :loading="loading"
          no-data-text="Nenalezeny žádné soutěže v tomto roce"
        >
          <!-- položky v menu -->
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :title="item.raw.label"
              :class="item.raw.event_closed ? 'bg-warning text-black' : ''"
            />
          </template>

          <!-- vybraný prvek (bez chipu) – obarvíme pozadí, když je zamčeno -->
          <template #selection="{ item }">
            <div :class="selectedLockEventObj?.event_closed ? 'bg-warning text-black px-2 py-1 rounded' : ''">
              <v-icon size="small" class="mr-1" v-if="selectedLockEventObj?.event_closed">mdi-lock</v-icon>
              <span>{{ item.title }}</span>
            </div>
          </template>
        </v-select>

      </v-col>

      <v-col cols="12" md="3" class="d-flex align-end">
        <v-btn
          :color="lockButtonColor"
          :loading="postingLock"
          :disabled="!selectedEventLock"
          @click="toggleLock"
        >
          <v-icon class="mr-2">
            {{ selectedLockEventObj?.event_closed ? 'mdi-lock-open-variant' : 'mdi-lock' }}
          </v-icon>
          {{ lockButtonText }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Přepočet Global Rankingu -->
    <v-row class="mb-3">
      <v-col cols="6" md="2">
        <SelectYear v-model="selectedYear" />
      </v-col>

      <v-col cols="6" md="2">
        <SelectMonth v-model="selectedMonth" />
      </v-col>

      <v-col cols="12" md="3" class="d-flex align-end">
        <v-btn
          color="primary"
          :loading="postingGlobalRanking"
          :disabled="!selectedYear || !selectedMonth"
          @click="recalculateGlobalRanking"
        >
          Přepočítat světový ranking
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-3">
      <v-col cols="12" md="6">
        <v-select
          v-model="selectedRaceId"
          :items="raceOptions"
          :loading="loadingRaces"
          density="comfortable"
          label="Vyber závod"
          item-title="label"
          item-value="value"
          hide-details
          no-data-text="V tomto roce nebyly nalezeny závody"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" :title="item?.raw?.label" />
          </template>
        </v-select>
      </v-col>
        
      <v-col cols="12" md="2" class="d-flex align-end">
        <v-btn
          color="primary"
          :disabled="!selectedRaceId"
          @click="dialogOrganizers = true"
        >
          Upravit činovníky
        </v-btn>
      </v-col>
    </v-row>

    <v-col cols="12" md="2">
      <v-menu offset-y transition="slide-y-transition">
        <template v-slot:activator="{ props }">
          <v-btn color="green" text v-bind="props">
            {{ $t('event_settings.entries.exports') }} <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item :href="`${BaseUrl}/export/?type=txt&name=experience&year=${selectedYear}`"><v-list-item-title>Seznam praxe členů za rok {{ selectedYear }}</v-list-item-title></v-list-item>
          <v-list-item :href="`${BaseUrl}/export/?type=txt&name=green_jersey&year=${selectedYear}`"><v-list-item-title>Zelený trikot za rok {{ selectedYear }}</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </v-col>

    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="elevation-2">
          <v-card-title>Expirace rozhodčích</v-card-title>
          <v-data-table
            density="compact"
            :headers="classHeaders"
            :items="last15Referee"
            class="elevation-1"
            v-model:sort-by="sortByReferee"
            v-model:page="pageReferee"
            v-model:items-per-page="itemsPerPageReferee"
            :items-per-page-options="itemsPerPageOptions"
            :loading="loadingMembersAll"
            loading-text="general.loading"
            no-data-text="general.no_data"
          >
            <template v-slot:[`item.full_name`]="{ item }">
              <div class="d-flex align-center" style="white-space: nowrap;">
                <v-icon
                  class="mr-2"
                  color="green"
                  size="22"
                  :title="$t('members.edit_roles')"
                  @click="editRoles(item)"
                >mdi-account-hard-hat</v-icon>
                <router-link :to="'/ucet/' + item.id" target="_blank" rel="noopener noreferrer">
                  {{ item.full_name }}
                </router-link>
              </div>
            </template>
            <template v-slot:[`item.lvl_sort`]="{ item }">
              {{ toRomanLevel(item.lvl) }}.
            </template>
            <template v-slot:[`item.valid_from_sort`]="{ item }">
              {{ formatDate(item.valid_from) }}
            </template>
            <template v-slot:[`item.valid_to_sort`]="{ item }">
              {{ formatDate(item.valid_to) }}
            </template>
            <template v-slot:[`item.description`]="{ item }">
              <span style="white-space: nowrap;">{{ item.description }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
          <v-card class="elevation-2">
            <v-card-title>Přehled trenérů</v-card-title>
          <v-data-table
            density="compact"
            :headers="classHeaders"
            :items="last15Coach"
            class="elevation-1"
            v-model:sort-by="sortByCoach"
            v-model:page="pageCoach"
            v-model:items-per-page="itemsPerPageCoach"
            :items-per-page-options="itemsPerPageOptions"
            :loading="loadingMembersAll"
            loading-text="general.loading"
            no-data-text="general.no_data"
          >
            <template v-slot:[`item.full_name`]="{ item }">
              <div class="d-flex align-center" style="white-space: nowrap;">
                <v-icon
                  class="mr-2"
                  color="green"
                  size="22"
                  :title="$t('members.edit_roles')"
                  @click="editRoles(item)"
                >mdi-account-hard-hat</v-icon>
                <router-link :to="'/ucet/' + item.id" target="_blank" rel="noopener noreferrer">
                  {{ item.full_name }}
                </router-link>
              </div>
            </template>
            <template v-slot:[`item.lvl_sort`]="{ item }">
              {{ toRomanLevel(item.lvl) }}.
            </template>
            <template v-slot:[`item.valid_from_sort`]="{ item }">
              {{ formatDate(item.valid_from) }}
            </template>
            <template v-slot:[`item.valid_to_sort`]="{ item }">
              {{ item.valid_to ? formatDate(item.valid_to) : '∞' }}
            </template>
            <template v-slot:[`item.description`]="{ item }">
              <span style="white-space: nowrap;">{{ item.description }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>


    <DashboardPageRaceOrganizerList
      v-model="dialogOrganizers"
      :race-id="selectedRaceId"
      :race-name="raceOptions.find(r => r.value === selectedRaceId)?.label || ''"
    />

    <RoleDialog
      v-model="roleDialogOpen"
      :account-id="selectedAccountId"
      @close-dialog="closeAndReloadRoles"
    />


  </v-container>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import SelectYear from '@/components/shared/SelectYear.vue'
import apiClient from '/api.js'
import { useDateTimeFormat } from '@/composables/useDateTimeFormat'
import {useSnackbarStore} from '@/stores/snackbarStore'
import DashboardPageRaceOrganizerList from '@/components/DashboardPageRaceOrganizerList.vue'
import SelectMonth from '@/components/shared/SelectMonth.vue'
import RoleDialog from '@/components/shared/RoleDialog.vue'



const { formatShortDate, formatDate } = useDateTimeFormat()
const BaseUrl = ref(process.env.VUE_APP_API_BASE_URL.replace(/\/+$/, '').replace(/\/api\/?$/, '')); 

const snackbar = useSnackbarStore()
const selectedYear = ref(null)
const events = ref([])
const eventOptions = ref([])
const selectedEvent = ref(null)
const loading = ref(false)
const posting = ref(false)
const postingGlobalRanking = ref(false)

// ===== Přehled tříd (dashboard) =====
const loadingMembersAll = ref(false)
const membersAll = ref([])

const toRomanLevel = (lvl) => {
  const v = String(lvl ?? '').trim()
  if (v === '1') return 'I'
  if (v === '2') return 'II'
  if (v === '3') return 'III'
  return v
}

const toSortNum = (d) => {
  if (!d) return -Infinity
  const t = Date.parse(d)
  return Number.isFinite(t) ? t : -Infinity
}

const sortByReferee = ref([{ key: 'valid_to_sort', order: 'asc' }])
const sortByCoach = ref([{ key: 'valid_from_sort', order: 'desc' }])
const pageReferee = ref(1)
const pageCoach = ref(1)
const itemsPerPageReferee = ref(15)
const itemsPerPageCoach = ref(15)
const itemsPerPageOptions = [
  { value: 5, title: '5' },
  { value: 10, title: '10' },
  { value: 15, title: '15' },
]

const classHeaders = [
  { title: 'Jméno', key: 'full_name', align: 'left', sortable: true },
  { title: 'Třída', key: 'lvl_sort', align: 'left', width: '30px', sortable: true },
  { title: 'Platí od', key: 'valid_from_sort', align: 'left', sortable: true },
  { title: 'Platí do', key: 'valid_to_sort', align: 'left', sortable: true },
  { title: 'Pozn.', key: 'description', align: 'left', sortable: false },
]

const fetchMembersAll = async () => {
  loadingMembersAll.value = true
  try {
    const { data } = await apiClient.get('/members_all/')
    membersAll.value = data?.all_members || []
  } catch (e) {
    console.error(e)
    snackbar.error('Nepodařilo se načíst přehled tříd (members_all).')
    membersAll.value = []
  } finally {
    loadingMembersAll.value = false
  }
}

const last15Referee = computed(() => {
  return (membersAll.value || [])
    .filter(m => !!m?.referee)
    .map(m => ({
      id: m.id,
      full_name: `${m.last_name} ${m.first_name}`,
      lvl_sort: Number(m?.referee?.lvl ?? 0),
      valid_from_sort: toSortNum(m?.referee?.valid_from),
      valid_to_sort: m?.referee?.valid_to ? toSortNum(m.referee.valid_to) : Infinity,
      ...m.referee,
    }))
    .slice()
    .sort((a, b) => {
      const aHas = !!a.valid_to
      const bHas = !!b.valid_to
      if (aHas && bHas) {
        return toSortNum(a.valid_to) - toSortNum(b.valid_to) // nejstarší platnost do první
      }
      if (!aHas && !bHas) {
        return toSortNum(a.valid_from) - toSortNum(b.valid_from)
      }
      return aHas ? -1 : 1
    })
    .slice(0, 15)
})

const last15Coach = computed(() => {
  return (membersAll.value || [])
    .filter(m => !!m?.coach)
    .map(m => ({
      id: m.id,
      full_name: `${m.last_name} ${m.first_name}`,
      lvl_sort: Number(m?.coach?.lvl ?? 0),
      valid_from_sort: toSortNum(m?.coach?.valid_from),
      valid_to_sort: toSortNum(m?.coach?.valid_to),
      ...m.coach,
    }))
    .slice()
    .sort((a, b) => toSortNum(b.valid_from) - toSortNum(a.valid_from)) // nejnovější platí od první
    .slice(0, 15)
})



const fetchEvents = async (year) => {
  loading.value = true
  selectedEvent.value = null
  selectedEventLock.value = null
  try {
    const { data } = await apiClient.get(`/event/?year=${year}&period=all`)
    events.value = data
      .slice()
      .sort((a, b) => new Date(b.event_date_start) - new Date(a.event_date_start))

    eventOptions.value = events.value
      .filter(e => e.results != null) // zobraz jen ty eventy, které mají výsledky
      .map(e => ({
        label: `${formatShortDate(e.event_date_start)}-${formatShortDate(e.event_date_end || e.event_date_start)}— ${e.event_name}`,
        value: e.id,
      }))
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const recalculateVtRank = async () => {
  if (!selectedEvent.value) return
  posting.value = true
  try {
    const { data } = await apiClient.post(`event/${selectedEvent.value}/recalculate/`)
    const changed = data?.results_changed ?? 0
    const races_num = data?.races_processed ?? 0
    snackbar.success(`Výkonnostní třídy byly přepočítány. Počet změn: ${changed} v počtu závodů: ${races_num}.`);
  } catch (e) {
    console.error(e)
  } finally {
    posting.value = false
  }
}



// Lock/Unlock
const selectedEventLock = ref(null)
const postingLock = ref(false)


// Select pro uzamykání – použije VŠECHNY events (už načtené)
const eventOptionsLock = computed(() => {
  const today = new Date()
  return events.value
    .filter(e => new Date(e.event_date_start) <= today) // jen ne-budoucí
    .map(e => ({
      label: `${formatShortDate(e.event_date_start)}-${formatShortDate(e.event_date_end || e.event_date_start)} — ${e.event_name}`,
      value: e.id,
      event_closed: !!e.event_closed,
    }))
})

const selectedLockEventObj = computed(() =>
  events.value.find(e => e.id === selectedEventLock.value) || null
)

const lockButtonText = computed(() => {
  const ev = selectedLockEventObj.value
  if (!ev) return 'Uzamknout / Odemknout'
  return ev.event_closed ? 'Odemknout soutěž' : 'Uzamknout soutěž'
})

const lockButtonColor = computed(() => {
  const ev = selectedLockEventObj.value
  if (!ev) return 'primary'
  return ev.event_closed ? 'primary' : 'warning'
})

const toggleLock = async () => {
  const ev = selectedLockEventObj.value
  if (!ev) return
  postingLock.value = true
  try {
    // Backend invertuje stav podle event_id
    await apiClient.patch(`event/edit/?event_id=${ev.id}`)
    // UI hned flipneme lokálně
    ev.event_closed = !ev.event_closed
    snackbar.success(ev.event_closed ? 'Soutěž byla uzamčena.' : 'Soutěž byla odemčena.')
  } catch (e) {
    console.error(e)
    // Backend vrací 403/423 s .error — zobraz to, když je
    const msg = e?.response?.data?.error || 'Nepodařilo se změnit stav soutěže.'
    snackbar.error(msg)
  } finally {
    postingLock.value = false
  }
}


// ČINOVNÍCI A ORGANIZÁTOŘI

const raceOptions = ref([])      
const selectedRaceId = ref(null)
const dialogOrganizers = ref(false)
const loadingRaces = ref(false)

const fetchRaces = async (year) => {
  if (!year) return
  loadingRaces.value = true
  selectedRaceId.value = null
  try {
    const { data } = await apiClient.get(`/race/?year=${year}&type=all`)
    raceOptions.value = (data || []).map(r => ({
      value: r.id,
      label: `${formatShortDate(r.race_date)} ${r['event__event_name']} — ${r.race_name}`,
    }))
  } catch (e) {
    console.error(e)
    raceOptions.value = []
  } finally {
    loadingRaces.value = false
  }
}


// ***** Recalculate Wrold ranking *****

const selectedMonth = ref(null)


const recalculateGlobalRanking = async () => {
  if (!selectedYear.value || !selectedMonth.value) return

  // zákaz: aktuální měsíc nebo budoucnost
  const y = Number(selectedYear.value)
  const m = Number(selectedMonth.value)

  const now = new Date()
  const curY = now.getFullYear()
  const curM = now.getMonth() + 1

  const selectedYM = y * 12 + (m - 1)
  const currentYM  = curY * 12 + (curM - 1)

  if (selectedYM >= currentYM) {
    snackbar.error('Nelze přepočítat světový ranking pro aktuální nebo budoucí měsíc. Vyber měsíc v minulosti.')
    return
  }

  postingGlobalRanking.value = true
  try {
    await apiClient.post('/ranking/world/', {
      year: Number(selectedYear.value),
      month: Number(selectedMonth.value),
      backfill: 0, // případně nastav dle UI
    })
    snackbar.success(`Světový ranking byl přepočítán pro ${selectedMonth.value}.${selectedYear.value}.`)
  } catch (e) {
    console.error(e)
    const msg = e?.response?.data?.error || 'Nepodařilo se přepočítat světový ranking.'
    snackbar.error(msg)
  } finally {
    postingGlobalRanking.value = false
  }
}



const roleDialogOpen = ref(false)
const selectedAccountId = ref(-1)

const editRoles = (item) => {
  selectedAccountId.value = item.id
  roleDialogOpen.value = true
}

const closeAndReloadRoles = () => {
  roleDialogOpen.value = false
  fetchMembersAll()
}

onMounted(() => {
  selectedYear.value = new Date().getFullYear()
})

watch(selectedYear, async (y) => {
  if (!y) return
  // Nejdřív ostatní data pro dashboard, až potom members_all
  await fetchEvents(y)
  await fetchRaces(y)
  await fetchMembersAll()
})

</script>



